import { getFirestore, getCloudStorage, getFirestoreDeleteField } from '../../system/firebase';

import { SNACK_BAR_MESSAGE_OPERATION_FAILED, SNACK_BAR_MESSAGE_SAVE, UPLOADED_VIDEO_STATUS_PROCESSING, UPLOADED_VIDEO_STATUS_APPROVED } from '../constants';
import { mapDefinedFields, deleteEmptyFields } from '../dtoHelper';

import { format as formatDate } from 'date-fns';

import _get from 'lodash/get';
import _set from 'lodash/set';
import _isEmpty from 'lodash/isEmpty';
import _toLower from 'lodash/toLower';

export const loadCountriesList = setCountriesList => {
    const db = getFirestore();
    const countriesRef = db.collection('app');
    return countriesRef.onSnapshot(docSnapshot => {
        setCountriesList(docSnapshot.docs.map(doc => doc.data())[0].countryList);
    });
};

export const loadStatesList = async (setStatesList, country) => {
    const db = getFirestore();
    const statesRef = db.doc(`app/lovs/countries/${country}`);

    const doc = await statesRef.get();
    doc.exists && setStatesList(doc.data().stateList);
};

export const loadCitiesList = async (setCitiesList, country, province) => {
    const db = getFirestore();
    const countriesRef = db.doc(`app/lovs/countries/${country}/states/${province}`);

    const doc = await countriesRef.get();
    doc.exists && setCitiesList(doc.data().cityList);
};

export const updateUserData = async ({ data, userId, setSnackbarMessage, setSnackbarErrorMessage, snackBarMessage }) => {
    const db = getFirestore();
    const batch = db.batch();
    const currentDt = new Date();
    const userName = data?.userName;
    const userNameLower = userName ? _toLower(userName) : '';
    const userRef = db.collection('users').doc(userId);
    try {
        //------------------------------------------------------------------------------------
        // Update user doc
        //------------------------------------------------------------------------------------
        batch.set(userRef, mapDefinedFields({ ...data, userNameLower, updateDt: currentDt }), { merge: true });

        //------------------------------------------------------------------------------------
        // Add new LOV options (if any)
        //------------------------------------------------------------------------------------
        const newLovOptionRef = db.collection('newLovOptions').doc();
        const newLovOptionsDto = {};
        if (_get(data, 'newCity')) {
            _set(newLovOptionsDto, 'newCityOption', _get(data, 'newCity'));
            _set(newLovOptionsDto, 'newCityOptionState', _get(data, 'state', 'Unknown State'));
            _set(newLovOptionsDto, 'newCityOptionCountry', _get(data, 'country', 'Unknown Country'));
        }
        if (!_isEmpty(newLovOptionsDto)) {
            batch.set(newLovOptionRef, {
                ...newLovOptionsDto,
                createDt: currentDt,
                createBy: userId,
            });
        }

        //------------------------------------------------------------------------------------
        // commit changes
        //------------------------------------------------------------------------------------
        await batch.commit();

        setSnackbarMessage(snackBarMessage ? snackBarMessage : SNACK_BAR_MESSAGE_SAVE);
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const uploadProfilePicture = async (file, userId, setProfilePicture, data, setSnackbarMessage, setSnackbarErrorMessage, setProgress, closeDialog) => {
    const db = getFirestore();
    const now = new Date();

    const timestampStr = formatDate(now, 'yyyyMMddHHmmss_T');
    var storageRef = getCloudStorage().ref();
    var picRef = storageRef.child(`users/${userId}/profilePicture/${timestampStr}`);
    const uploadTask = picRef.put(file, {
        contentType: file.type,
        customMetadata: { userId, origFilename: timestampStr, isJsProfilePic: true },
    });

    const userRef = db.collection('users').doc(userId);
    const uploadedImagesRef = db.collection('uploadedImages').doc();

    uploadTask.on(
        'state_changed',
        snapshot => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setProgress(progress);
        },
        error => console.error('File upload failed', error),
        async () => {
            try {
                const url = await picRef.getDownloadURL();
                closeDialog();
                setProgress(null);
                setProfilePicture(url);

                //------------------------------------------------------------------------------------
                // creating Uploaded Image Doc
                //------------------------------------------------------------------------------------
                await uploadedImagesRef.set({
                    createDt: now,
                    isJsProfilePic: true,
                    status: UPLOADED_VIDEO_STATUS_PROCESSING, // P, F, A, C, D
                    updateDt: now,
                    url,
                    userId,
                });

                //------------------------------------------------------------------------------------
                // Updating User Doc
                //------------------------------------------------------------------------------------

                await userRef.update({
                    //  profilePicUrl: url,  // not saving because of processing first
                    updateDt: now,
                    uploadedPictureStatus: UPLOADED_VIDEO_STATUS_PROCESSING, // added to display processing indicator
                });

                setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
            } catch (error) {
                console.log(error);
                setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
            }
        },
    );
};

export const deleteProfilePicture = async (userId, setSnackbarMessage, setSnackbarErrorMessage, prevPicUrl) => {
    const db = getFirestore();
    const now = new Date();
    const timeStamp = prevPicUrl.split('%2F')[3].split('?alt')[0];
    var storageRef = getCloudStorage().ref();
    var picRef = storageRef.child(`users/${userId}/profilePicture/${timeStamp}`);
    const userRef = db.collection('users').doc(userId);

    try {
        await userRef.update({ profilePicUrl: getFirestoreDeleteField(), updateDt: now });
        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);

        await picRef.delete();
    } catch (error) {
        console.log(error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const checkUserName = async ({ userName }) => {
    const db = getFirestore();
    const query = db.collection('users').where('userNameLower', '==', _toLower(userName)).limit(1);
    try {
        const querySnapshot = await query.get();
        const count = querySnapshot.size;
        return count > 0;
    } catch (error) {
        console.log('error', error);
        throw error;
    }
};

export const resetJsSeResults = ({ userId, setSnackbarMessage, setSnackbarErrorMessage }) => {
    const db = getFirestore();

    const userRef = db.collection('users').doc(userId);

    return db
        .runTransaction(transaction => {
            // This code may get re-run multiple times if there are conflicts.
            return transaction.get(userRef).then(function (doc) {
                if (!doc.exists) {
                    throw new Error('Profile document does not exist!');
                }

                const dto = {
                    seResult: null, // for removing stats old of bad video.
                    uploadedPictureStatus: UPLOADED_VIDEO_STATUS_APPROVED, // for reseting the video status
                };

                transaction.update(userRef, {
                    ...deleteEmptyFields(dto),
                    updateDt: new Date(),
                });
            });
        })
        .then(function () {
            // console.log('Transaction successfully committed!');
            // dataDidSave();
            setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
        })
        .catch(function (error) {
            console.error('Save profile type transaction failed: ', error);
            setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        });
};
